import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2, Pencil, Trash2, X, Check, Plus, Shield, Users, Calendar, LayoutGrid } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminPage() {
  const [tab, setTab] = useState('resources');
  const [resources, setResources] = useState([]);
  const [users, setUsers] = useState([]);
  const [allBookings, setAllBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [selectedResource, setSelectedResource] = useState(null);
  const [resourceForm, setResourceForm] = useState({ name: '', description: '', capacity: '' });
  const [selectedUser, setSelectedUser] = useState(null);
  const [userForm, setUserForm] = useState({ name: '', email: '', role: 'user', password: '' });
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bookingForm, setBookingForm] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchResources();
  }, []);

  useEffect(() => {
    if (tab === 'users') fetchUsers();
    if (tab === 'bookings') fetchBookings();
  }, [tab]);

  const fetchResources = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/resources`, { withCredentials: true });
      setResources(data);
    } finally { setLoading(false); }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/users`, { withCredentials: true });
      setUsers(data);
    } catch { /* admin only */ } finally { setLoading(false); }
  };

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/bookings`, { withCredentials: true });
      setAllBookings(data);
    } catch { /* admin only */ } finally { setLoading(false); }
  };

  const handleChange = (field, val) => setResourceForm(f => ({ ...f, [field]: val }));
  const handleUserChange = (field, val) => setUserForm(f => ({ ...f, [field]: val }));
  const handleBookingChange = (field, val) => setBookingForm(f => ({ ...f, [field]: val }));

  // ─── RESOURCES ───────────────────────────────────────────────
  const handleSaveResource = async () => {
    setSaving(true);
    try {
      if (selectedResource) {
        await axios.put(`${API}/api/resources/${selectedResource}`, resourceForm, { withCredentials: true });
      } else {
        await axios.post(`${API}/api/resources`, resourceForm, { withCredentials: true });
      }
      setSelectedResource(null);
      setResourceForm({ name: '', description: '', capacity: '' });
      fetchResources();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  const handleEditResource = (r) => {
    setSelectedResource(r.id);
    setResourceForm({ name: r.name, description: r.description || '', capacity: r.capacity || '' });
  };

  const handleDeleteResource = async (id) => {
    setDeleting(id);
    try {
      await axios.delete(`${API}/api/resources/${id}`, { withCredentials: true });
      fetchResources();
    } finally { setDeleting(null); }
  };

  // ─── USERS ───────────────────────────────────────────────────
  const handleSaveUser = async () => {
    setSaving(true);
    try {
      if (selectedUser) {
        await axios.put(`${API}/api/admin/users/${selectedUser}`, userForm, { withCredentials: true });
      }
      setSelectedUser(null);
      setUserForm({ name: '', email: '', role: 'user', password: '' });
      fetchUsers();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  const handleEditUser = (u) => {
    setSelectedUser(u.id);
    setUserForm({ name: u.name, email: u.email, role: u.role, password: '' });
  };

  const handleDeleteUser = async (id) => {
    setDeleting(id);
    try {
      await axios.delete(`${API}/api/admin/users/${id}`, { withCredentials: true });
      fetchUsers();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed');
    } finally { setDeleting(null); }
  };

  // ─── BOOKINGS ────────────────────────────────────────────────
  const handleSaveBooking = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/bookings/${selectedBooking}`, bookingForm, { withCredentials: true });
      setSelectedBooking(null);
      setBookingForm({});
      fetchBookings();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  const handleEditBooking = (b) => {
    setSelectedBooking(b.id);
    setBookingForm({ date: b.date, time_slot: b.time_slot, status: b.status, resource_id: b.resource_id });
  };

  const handleDeleteBooking = async (id) => {
    setDeleting(id);
    try {
      await axios.delete(`${API}/api/admin/bookings/${id}`, { withCredentials: true });
      fetchBookings();
    } finally { setDeleting(null); }
  };

  const TABS = [
    { id: 'resources', label: 'Resources', icon: LayoutGrid },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'bookings', label: 'Bookings', icon: Calendar },
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="admin-page">
      <div className="mb-8">
        <h1 className="font-heading text-3xl sm:text-4xl font-medium tracking-tighter text-white">Admin</h1>
        <p className="text-text-secondary text-sm mt-2 font-body">Manage all resources, users, and bookings.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-8 border-b border-border/20">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => { setTab(t.id); setSelectedResource(null); setSelectedUser(null); setSelectedBooking(null); }}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-mono border-b-2 transition-all ${
              tab === t.id
                ? 'border-white text-white'
                : 'border-transparent text-text-secondary hover:text-white'
            }`}
          >
            <t.icon size={14} />
            {t.label}
          </button>
        ))}
      </div>

      {/* ─── RESOURCES ─────────────────────────────────────── */}
      {tab === 'resources' && (
        <>
          {/* Add / Edit form */}
          <div className="mb-8 p-5 border border-border/40 bg-surface/30">
            <h2 className="text-xs font-mono uppercase tracking-wider text-text-secondary mb-4">
              {selectedResource ? 'Edit Resource' : 'Add Resource'}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <input
                placeholder="Name *"
                value={resourceForm.name}
                onChange={e => handleChange('name', e.target.value)}
                className="bg-surface border border-border/40 text-white text-sm px-3 py-2.5 focus:outline-none focus:border-white/40"
              />
              <input
                placeholder="Description"
                value={resourceForm.description}
                onChange={e => handleChange('description', e.target.value)}
                className="bg-surface border border-border/40 text-white text-sm px-3 py-2.5 focus:outline-none focus:border-white/40"
              />
              <input
                placeholder="Capacity"
                value={resourceForm.capacity}
                onChange={e => handleChange('capacity', e.target.value)}
                className="bg-surface border border-border/40 text-white text-sm px-3 py-2.5 focus:outline-none focus:border-white/40"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSaveResource}
                disabled={!resourceForm.name || saving}
                className="flex items-center gap-2 px-4 py-2 bg-white text-black text-sm font-mono hover:opacity-90 transition-all disabled:opacity-40"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                {selectedResource ? 'Update' : 'Add'}
              </button>
              {selectedResource && (
                <button
                  onClick={() => { setSelectedResource(null); setResourceForm({ name: '', description: '', capacity: '' }); }}
                  className="flex items-center gap-2 px-4 py-2 border border-border/40 text-text-secondary text-sm font-mono hover:text-white transition-all"
                >
                  <X size={14} /> Cancel
                </button>
              )}
            </div>
          </div>

          {/* List */}
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 size={20} className="animate-spin text-text-secondary" /></div>
          ) : (
            <div className="space-y-1">
              {resources.map(r => (
                <div key={r.id} className="flex items-center justify-between px-4 py-3 border border-border/20 bg-surface/20 hover:bg-surface/30 transition-all" data-testid={`resource-row-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-mono text-white">{r.name}</div>
                    <div className="text-xs text-text-secondary font-body truncate">{r.description || 'No description'}</div>
                  </div>
                  <div className="flex items-center gap-1 ml-4">
                    <button onClick={() => handleEditResource(r)} className="p-2 border border-border/40 text-text-secondary hover:text-white hover:border-white/30 transition-all" data-testid={`edit-resource-${r.id}`}>
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => handleDeleteResource(r.id)} disabled={deleting === r.id} className="p-2 border border-booked/30 text-booked hover:bg-booked/10 transition-all disabled:opacity-40" data-testid={`delete-resource-${r.id}`}>
                      {deleting === r.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ─── USERS ─────────────────────────────────────────── */}
      {tab === 'users' && (
        <>
          {/* Edit form */}
          {selectedUser && (
            <div className="mb-8 p-5 border border-border/40 bg-surface/30">
              <h2 className="text-xs font-mono uppercase tracking-wider text-text-secondary mb-4">Edit User</h2>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4">
                <input
                  placeholder="Name"
                  value={userForm.name}
                  onChange={e => handleUserChange('name', e.target.value)}
                  className="bg-surface border border-border/40 text-white text-sm px-3 py-2.5 focus:outline-none focus:border-white/40"
                />
                <input
                  placeholder="Email"
                  value={userForm.email}
                  onChange={e => handleUserChange('email', e.target.value)}
                  className="bg-surface border border-border/40 text-white text-sm px-3 py-2.5 focus:outline-none focus:border-white/40"
                  disabled
                />
                <select
                  value={userForm.role}
                  onChange={e => handleUserChange('role', e.target.value)}
                  className="bg-surface border border-border/40 text-white text-sm px-3 py-2.5 focus:outline-none focus:border-white/40"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
                <input
                  placeholder="New password (leave blank to keep)"
                  type="password"
                  value={userForm.password}
                  onChange={e => handleUserChange('password', e.target.value)}
                  className="bg-surface border border-border/40 text-white text-sm px-3 py-2.5 focus:outline-none focus:border-white/40"
                />
              </div>
              <div className="flex gap-2">
                <button onClick={handleSaveUser} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-white text-black text-sm font-mono hover:opacity-90 transition-all disabled:opacity-40">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Update
                </button>
                <button onClick={() => { setSelectedUser(null); setUserForm({ name: '', email: '', role: 'user', password: '' }); }} className="flex items-center gap-2 px-4 py-2 border border-border/40 text-text-secondary text-sm font-mono hover:text-white transition-all">
                  <X size={14} /> Cancel
                </button>
              </div>
            </div>
          )}

          {/* Users table */}
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 size={20} className="animate-spin text-text-secondary" /></div>
          ) : (
            <div className="border border-border/40 overflow-hidden">
              <div className="hidden sm:grid grid-cols-5 gap-4 px-4 py-2.5 bg-surface border-b border-border/40 text-xs font-mono uppercase tracking-wider text-text-secondary">
                <span>Name</span>
                <span>Email</span>
                <span>Role</span>
                <span>Created</span>
                <span className="text-right">Actions</span>
              </div>
              {users.map(u => (
                <div key={u.id} className="grid grid-cols-1 sm:grid-cols-5 gap-2 sm:gap-4 px-4 py-3 border-b border-border/20 text-sm hover:bg-surface/20 transition-all" data-testid={`user-row-${u.id}`}>
                  <span className="text-white font-body truncate">{u.name || '—'}</span>
                  <span className="text-text-secondary font-mono text-xs truncate">{u.email}</span>
                  <span className={`font-mono text-xs uppercase ${u.role === 'admin' ? 'text-accent' : 'text-text-secondary'}`}>
                    {u.role === 'admin' && <Shield size={10} className="inline mr-1" />}
                    {u.role}
                  </span>
                  <span className="text-text-secondary font-mono text-xs">{u.created_at ? u.created_at.split('T')[0] : '—'}</span>
                  <div className="flex justify-end gap-1">
                    <button onClick={() => handleEditUser(u)} className="p-2 border border-border/40 text-text-secondary hover:text-white hover:border-white/30 transition-all" data-testid={`edit-user-${u.id}`}>
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => handleDeleteUser(u.id)} disabled={deleting === u.id} className="p-2 border border-booked/30 text-booked hover:bg-booked/10 transition-all disabled:opacity-40" data-testid={`delete-user-${u.id}`}>
                      {deleting === u.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ─── BOOKINGS ──────────────────────────────────────── */}
      {tab === 'bookings' && (
        <>
          {/* Edit form */}
          {selectedBooking && (
            <div className="mb-8 p-5 border border-border/40 bg-surface/30">
              <h2 className="text-xs font-mono uppercase tracking-wider text-text-secondary mb-4">Edit Booking</h2>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4">
                <input
                  type="date"
                  value={bookingForm.date || ''}
                  onChange={e => handleBookingChange('date', e.target.value)}
                  className="bg-surface border border-border/40 text-white text-sm px-3 py-2.5 focus:outline-none focus:border-white/40"
                />
                <input
                  placeholder="Time slot (e.g. 10:00)"
                  value={bookingForm.time_slot || ''}
                  onChange={e => handleBookingChange('time_slot', e.target.value)}
                  className="bg-surface border border-border/40 text-white text-sm px-3 py-2.5 focus:outline-none focus:border-white/40"
                />
                <select
                  value={bookingForm.status || 'confirmed'}
                  onChange={e => handleBookingChange('status', e.target.value)}
                  className="bg-surface border border-border/40 text-white text-sm px-3 py-2.5 focus:outline-none focus:border-white/40"
                >
                  <option value="confirmed">Confirmed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <input
                  placeholder="Resource ID"
                  value={bookingForm.resource_id || ''}
                  onChange={e => handleBookingChange('resource_id', e.target.value)}
                  className="bg-surface border border-border/40 text-white text-sm px-3 py-2.5 focus:outline-none focus:border-white/40"
                />
              </div>
              <div className="flex gap-2">
                <button onClick={handleSaveBooking} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-white text-black text-sm font-mono hover:opacity-90 transition-all disabled:opacity-40">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Update
                </button>
                <button onClick={() => { setSelectedBooking(null); setBookingForm({}); }} className="flex items-center gap-2 px-4 py-2 border border-border/40 text-text-secondary text-sm font-mono hover:text-white transition-all">
                  <X size={14} /> Cancel
                </button>
              </div>
            </div>
          )}

          {/* Bookings table */}
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 size={20} className="animate-spin text-text-secondary" /></div>
          ) : (
            <div className="border border-border/40 overflow-hidden overflow-x-auto">
              <div className="hidden xl:grid grid-cols-6 gap-4 px-4 py-2.5 bg-surface border-b border-border/40 text-xs font-mono uppercase tracking-wider text-text-secondary min-w-[700px]">
                <span>Resource</span>
                <span>User</span>
                <span>Date</span>
                <span>Time</span>
                <span>Status</span>
                <span className="text-right">Actions</span>
              </div>
              {allBookings.length === 0 ? (
                <p className="text-text-secondary text-center py-12 text-sm font-body">No bookings found.</p>
              ) : (
                allBookings.map(b => (
                  <div key={b.id} className="grid grid-cols-2 xl:grid-cols-6 gap-2 xl:gap-4 px-4 py-3 border-b border-border/20 text-sm hover:bg-surface/20 transition-all" data-testid={`booking-row-${b.id}`}>
                    <span className="text-white font-body truncate">{b.resource_name || b.resource_id}</span>
                    <span className="text-text-secondary font-mono text-xs truncate">{b.user_email}</span>
                    <span className="text-text-secondary font-mono text-xs">{b.date}</span>
                    <span className="text-text-secondary font-mono text-xs">{b.time_slot}</span>
                    <span className={`font-mono text-xs uppercase ${b.status === 'confirmed' ? 'text-available' : 'text-booked'}`}>{b.status}</span>
                    <div className="flex justify-end gap-1 col-span-2 xl:col-span-1">
                      <button onClick={() => handleEditBooking(b)} className="p-2 border border-border/40 text-text-secondary hover:text-white hover:border-white/30 transition-all" data-testid={`edit-booking-${b.id}`}>
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => handleDeleteBooking(b.id)} disabled={deleting === b.id} className="p-2 border border-booked/30 text-booked hover:bg-booked/10 transition-all disabled:opacity-40" data-testid={`delete-booking-${b.id}`}>
                        {deleting === b.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
